/* eslint-disable jsx-a11y/no-static-element-interactions */
import React from 'react';

import { Icon } from 'design-system-web';
import cx from 'classnames';
import { format } from 'date-fns';

import { LaneType } from 'common-types';
import {
  AttachmentPreview,
  AttachmentResponse,
  AttachmentImageContentTypeEnum,
  AttachmentDocumentContentTypeEnum,
} from 'lane-shared/types/attachment';

import { S } from 'components/typography';

import crossIcon from 'static/img/xmark-large.svg';

import styles from './AttachmentThumbnail.scss';

const HEIC_MIME_TYPES = ['image/heic', 'image/heif'];
const HEIC_EXTENSION_PATTERN = /\.(heic|heif)$/i;

type Props = {
  attachment: AttachmentPreview | AttachmentResponse;
  deleteAttachmentHandler: (attachmentId: LaneType.UUID) => void;
  preventDelete?: boolean;
  attachmentThumbnailStyle?: string;
  disableDeleteWarning?: boolean;
  onPreviewModal?: any;
};

export function AttachmentThumbnail({
  attachment,
  deleteAttachmentHandler,
  preventDelete = false,
  disableDeleteWarning = false,
  attachmentThumbnailStyle,
  onPreviewModal,
}: Props) {
  const attachmentObj = attachment as any;

  const attachmentDate =
    attachmentObj.created &&
    format(Date.parse(attachmentObj.created), "PP 'at' p");

  // Before upload/conversion, a HEIC preview's thumbnailSignedUrl/fileUrl is
  // a local blob: URL of the raw file — browsers can't render that, so fall
  // back to a placeholder icon instead of a blank box until the real
  // (converted, server-hosted) thumbnail is available.
  const previewUrl = attachmentObj.thumbnailSignedUrl ?? attachmentObj.fileUrl;
  const isUnrenderableHeicPreview =
    typeof previewUrl === 'string' &&
    previewUrl.startsWith('blob:') &&
    (HEIC_MIME_TYPES.includes((attachmentObj.file.type ?? '').toLowerCase()) ||
      HEIC_EXTENSION_PATTERN.test(attachmentObj.file.name ?? ''));

  const isDocument = Object.values(AttachmentDocumentContentTypeEnum).includes(
    attachmentObj.file.type
  );
  const thumbnailBackgroundUrl = attachmentObj.thumbnailSignedUrl
    ? attachmentObj.thumbnailSignedUrl
    : attachmentObj.fileUrl;

  const generateFileType = (type: string) => {
    if (
      Object.values(AttachmentImageContentTypeEnum).toString().includes(type)
    ) {
      return 'Image';
    }

    if (type === AttachmentDocumentContentTypeEnum.pdf) {
      return 'PDF';
    }

    if (type === AttachmentDocumentContentTypeEnum.csv) {
      return 'CSV';
    }

    return null;
  };

  async function onDeleteAttachment() {
    try {
      if (!disableDeleteWarning) {
        await window.Alert.confirm({
          title: `Delete this attachment?`,
          message: `Are you sure you want to delete this? Once deleted you cannot get it back.`,
          confirmText: 'Yes',
        });
      }
      // FIXME: Log error for datadog, missing stack trace
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      return;
    }

    deleteAttachmentHandler(attachment.id);
  }

  return (
    <div
      data-test="attachmentThumbnail"
      className={cx(styles.AttachmentThumbnail, attachmentThumbnailStyle)}
    >
      <div
        className={styles.imageWrapper}
        style={{
          backgroundImage: isUnrenderableHeicPreview
            ? undefined
            : `url(${thumbnailBackgroundUrl})`,
        }}
        onClick={() => onPreviewModal?.(attachment.id)}
        data-test="attachmentThumbnailImage"
      >
        {isUnrenderableHeicPreview && (
          <Icon name="image" style={{ fontSize: '2em', color: '#52565B' }} />
        )}
        {!isUnrenderableHeicPreview && isDocument && (
          <Icon name="file" style={{ fontSize: '2em', color: '#52565B' }} />
        )}
      </div>
      <div className={styles.thumbnailData}>
        <S className={styles.thumbnailName} id="attachmentName">
          {attachmentObj.file.name}
          {/* {(attachment.file as any).name} */}
        </S>
        <S className={styles.thumbnailType} id="attachmentType">
          {generateFileType(attachmentObj.file.type)}
        </S>
        {attachmentObj.created && (
          <S className={styles.thumbnailType}>{attachmentDate}</S>
        )}
      </div>
      {!preventDelete && (
        <div
          data-test="deleteAttachment"
          role="button"
          className={styles.deleteIcon}
          onClick={onDeleteAttachment}
          tabIndex={0}
        >
          <img src={crossIcon} className={styles.icon} alt="crossIcon" />
        </div>
      )}
    </div>
  );
}
